/**
 * The Billing tab's refusals, read off the wire.
 *
 * The backend (`modules/billing/billing-errors.ts`) puts the bare code in the
 * error MESSAGE as well as `cause.code`, because tRPC 11.7.1 drops wrapped
 * causes until BLU-1277. So this matches the message, exactly, and maps it
 * to a kind the screen has words for. Keep the three strings in lockstep with
 * the backend file.
 */

export const BILLING_STRIPE_UNAVAILABLE = 'BILLING_STRIPE_UNAVAILABLE';
export const BILLING_PORTAL_UNAVAILABLE = 'BILLING_PORTAL_UNAVAILABLE';
export const BILLING_NOT_SET_UP = 'BILLING_NOT_SET_UP';

export type BillingErrorKind = 'stripe_unavailable' | 'portal_unavailable' | 'not_set_up';

const KIND_BY_CODE: Record<string, BillingErrorKind> = {
  [BILLING_STRIPE_UNAVAILABLE]: 'stripe_unavailable',
  [BILLING_PORTAL_UNAVAILABLE]: 'portal_unavailable',
  [BILLING_NOT_SET_UP]: 'not_set_up',
};

export function billingErrorKind(error: unknown): BillingErrorKind | null {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' ? (KIND_BY_CODE[message.trim()] ?? null) : null;
}

/**
 * Query retry for the Stripe-backed reads. A Billing refusal is final (the
 * backend already waited out its own 10 s Stripe timeout, and a retry is
 * another Stripe call), and a 4xx is final. Anything else (a dropped
 * connection) gets one quiet retry, not the app default of three, so a
 * Stripe outage reaches the Retry button in seconds rather than ~40 s.
 */
export function billingQueryRetry(failureCount: number, error: unknown): boolean {
  if (billingErrorKind(error)) return false;
  const httpStatus = (error as { data?: { httpStatus?: unknown } } | null)?.data?.httpStatus;
  if (typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500) return false;
  return failureCount < 1;
}
